﻿import { useState } from 'react';
import { motion } from 'motion/react';
import { Search, Github, Star, ExternalLink, Download, Loader2, Package, Globe } from 'lucide-react';
import { toast } from 'sonner';
import CustomSelect from './CustomSelect';

interface MCP搜索Result {
  id: number | string;
  name: string;
  description: string;
  stars: number;
  url: string;
  topics: string[];
  language: string;
  updatedAt: string;
}

type SearchSource = 'github' | 'npm';

const LANGUAGES = ['TypeScript', 'Python', 'JavaScript', 'Go', 'Rust', 'Java'];

export function GitHubMCPBrowser({ t, embedded = false }: { t?: any; embedded?: boolean }) {
  const [source, setSource] = useState<SearchSource>('github');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MCP搜索Result[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, set搜索ed] = useState(false);
  const [installing, setInstalling] = useState<string | null>(null);
  const [langFilter, set语言Filter] = useState('');
  const [sortBy, set排序By] = useState<'stars' | 'relevance'>('stars');

  const handle搜索 = async () => {
    if (!query.trim()) return;
    setLoading(true);
    set搜索ed(true);
    try {
      const endpoint = source === 'github'
        ? `/api/mcp/github/search?q=${encodeURIComponent(query.trim())}`
        : `/api/mcp/npm/search?q=${encodeURIComponent(query.trim())}`;
      const res = await fetch(endpoint);
      const data = await res.json();
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async (repo: MCP搜索Result) => {
    setInstalling(String(repo.name));
    try {
      const skillName = source === 'github'
        ? String(repo.name).split('/').pop() || String(repo.name)
        : String(repo.name);
      const body = source === 'github'
        ? {
            skillId: `github-${repo.id}`,
            skillName,
            installSource: 'github',
            repoUrl: repo.url,
          }
        : {
            skillId: `npm-${repo.name}`,
            skillName,
            installSource: 'npm',
            npmPackage: repo.name,
          };
      const res = await fetch('/api/marketplace/skills/acquire', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(data.error || data.message || '安装失败');
      }
      toast.success(data.message || t?.mcpInstallSuccess || '已安装并连接。');
    } catch (err: any) {
      toast.error(err?.message || t?.mcpInstallFailed || '此包无法作为可运行 MCP 技能安装。');
    } finally {
      setInstalling(null);
    }
  };

  const changeSource = (s: SearchSource) => {
    setSource(s);
    setResults([]);
    set搜索ed(false);
    set语言Filter('');
  };

  const filtered = langFilter
    ? results.filter(r => r.language && r.language.toLowerCase() === langFilter.toLowerCase())
    : results;

  const sorted = sortBy === 'stars'
    ? [...filtered].sort((a, b) => b.stars - a.stars)
    : filtered;

  return (
    <div className={`${embedded ? 'min-h-[620px]' : 'h-full bg-zinc-950/60 backdrop-blur-xl'} flex flex-col text-white overflow-y-auto`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-white/5">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
          {source === 'github' ? (
            <Github size={20} className="text-purple-400" />
          ) : (
            <Globe size={20} className="text-purple-400" />
          )}
        </div>
        <div>
          <h2 className="text-sm font-bold text-white/90">{t?.mcpBrowserTitle || 'MCP 浏览器'}</h2>
          <p className="text-xs text-white/55">{t?.mcpBrowserDesc || '发现并安装社区 MCP 服务器'}</p>
        </div>
      </div>

      <div className="flex-1 p-6 space-y-4">
        <div className="rounded-xl border border-amber-400/15 bg-amber-500/10 px-4 py-3 text-xs leading-relaxed text-amber-100/75">
          {t?.mcpInstallNotice || 'Only MCP-compatible packages can be installed. 小枫 will clone/install/start the package first; if it cannot run as an MCP server, it will fail with a clear message instead of pretending to be ready.'}
        </div>
        {/* Source tabs */}
        <div className="flex gap-1 p-1 rounded-xl bg-white/5 border border-white/5">
          <button
            onClick={() => changeSource('github')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
              source === 'github'
                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                : 'text-white/55 hover:text-white/50'
            }`}
          >
            <Github size={12} />
            {t?.mcpSourceGitHub || 'GitHub'}
          </button>
          <button
            onClick={() => changeSource('npm')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
              source === 'npm'
                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                : 'text-white/55 hover:text-white/50'
            }`}
          >
            <Package size={12} />
            {t?.mcpSourceNpm || 'npm'}
          </button>
        </div>

        {/* 搜索 bar */}
        <div className="flex gap-2">
          <div className="flex-1 flex items-center gap-2 px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl">
            <Search size={14} className="text-white/55" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handle搜索()}
              placeholder={
                source === 'github'
                  ? (t?.mcp搜索GitHub || '在 GitHub 搜索 MCP 服务器...')
                  : (t?.mcp搜索Npm || '在 npm 搜索 MCP 包...')
              }
              className="flex-1 bg-transparent text-white/80 text-sm outline-none placeholder:text-white/55"
            />
          </div>
          <button
            onClick={handle搜索}
            disabled={loading}
            className="px-4 py-2.5 rounded-xl bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-bold uppercase tracking-wider hover:bg-purple-500/30 transition-all disabled:opacity-40"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : (t?.mcp搜索Btn || '搜索')}
          </button>
        </div>

        {/* Filters — only show when results exist */}
        {searched && results.length > 0 && (
          <div className="flex items-center gap-3">
            {/* 排序 toggle */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/45 uppercase tracking-wider">{t?.sortBy || '排序'}</span>
              <button
                onClick={() => set排序By(sortBy === 'stars' ? 'relevance' : 'stars')}
                className="text-xs px-2 py-1 rounded-md bg-white/5 text-white/50 font-bold uppercase hover:bg-white/10 transition-all"
              >
                {sortBy === 'stars' ? (t?.mcp排序Stars || '星标') : (t?.mcp排序Relevance || '相关度')}
              </button>
            </div>

            {/* 语言uage filter — GitHub only */}
            {source === 'github' && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/45 uppercase tracking-wider">{t?.mcpFilter语言uage || '语言'}</span>
                <CustomSelect
                  value={langFilter}
                  onChange={v => set语言Filter(v)}
                  size="sm"
                  className="min-w-[100px]"
                  options={[
                    { value: '', label: t?.mcpFilterAll语言uages || '全部' },
                    ...LANGUAGES.map(l => ({ value: l, label: l })),
                  ]}
                />
              </div>
            )}
          </div>
        )}

        {/* Results */}
        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-white/55">
              <Loader2 size={24} className="animate-spin mr-2" /> {t?.mcp搜索ing || '搜索中...'}
            </div>
          ) : searched && results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-white/45">
              <Package size={40} className="mb-3 opacity-30" />
              <span className="text-xs font-bold uppercase tracking-widest">{t?.mcpNoServersFound || '未找到 MCP 服务器'}</span>
            </div>
          ) : (
            sorted.map(repo => (
              <motion.div
                key={String(repo.id)}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-all"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {source === 'github' ? (
                    <Github size={18} className="text-white/40" />
                  ) : (
                    <Package size={18} className="text-white/40" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-white/80 truncate">{String(repo.name)}</span>
                    {repo.stars > 0 && (
                      <div className="flex items-center gap-1 text-amber-400">
                        <Star size={12} fill="currentColor" />
                        <span className="text-xs font-bold">{repo.stars}</span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-white/40 mt-1 line-clamp-2">
                    {repo.description || t?.mcpNoDescription || '无描述'}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    {repo.language && (
                      <span className="text-xs px-1.5 py-0.5 rounded-md bg-blue-500/10 text-blue-400 font-bold uppercase">
                        {repo.language}
                      </span>
                    )}
                    {repo.topics.slice(0, 3).map(topic => (
                      <span key={topic} className="text-xs px-1.5 py-0.5 rounded-md bg-white/5 text-white/55 font-bold uppercase">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <a
                    href={repo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={t?.mcpViewOn ? `${t.mcpViewOn} ${source === 'github' ? 'GitHub' : 'npm'}` : `View on ${source}`}
                    className="p-2 rounded-lg text-white/55 hover:text-white/60 hover:bg-white/10 transition-all"
                  >
                    <ExternalLink size={14} />
                  </a>
                  <button
                    onClick={() => handleInstall(repo)}
                    disabled={installing === String(repo.name)}
                    className="p-2 rounded-lg text-emerald-400/60 hover:text-emerald-400 hover:bg-emerald-500/10 transition-all"
                  >
                    {installing === String(repo.name) ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                  </button>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
